import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAtlas } from "@/context/AtlasContext";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronsUpDown, Check, LifeBuoy, ChevronDown, LayoutGrid } from "lucide-react";
import { useModes, modeFor, isItemActive } from "@/components/shell/nav";
import { FavoritesList, RecentsList } from "@/components/shell/PrefsLists";

const ls = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };

export function ContextPanel({ mobile }) {
  const modes = useModes();
  const location = useLocation();
  const mode = modes.find((m) => m.key === modeFor(location.pathname).key) || modes[0];
  const [collapsed, setCollapsed] = useState(() => ls("to_nav_collapsed2", {}));
  const toggle = (k) => setCollapsed((c) => { const n = { ...c, [k]: !c[k] }; localStorage.setItem("to_nav_collapsed2", JSON.stringify(n)); return n; });
  if (!mode) return null;
  return (
    <div className={cn("h-full flex flex-col", mobile ? "w-full" : "w-56")} style={{ background: "var(--ink-2)" }} data-testid="context-panel">
      <BrandLockup />
      <WorkspaceSwitch />
      {mode.key === "atlas" && <FavoritesList compact />}
      <div className="px-4 pt-4 pb-1 flex items-center gap-2" data-testid="context-mode-label"><mode.icon className="w-3.5 h-3.5 text-slate-500" /><span className="eyebrow-light">{mode.label}</span></div>
      <nav className="flex-1 overflow-y-auto dark-scroll px-2 pb-3">
        {mode.sections.map((s, i) => {
          const closed = s.collapsible ? (collapsed[s.key] ?? true) : collapsed[s.key];
          const activeIn = s.items.some((n) => isItemActive(n, location.pathname));
          return (
            <div key={s.key || i} className={cn(i > 0 && "mt-3 pt-3 border-t border-white/[0.06]")}>
              {s.label && <button onClick={() => toggle(s.key)} className="w-full flex items-center justify-between px-3 py-1.5 group" data-testid={`nav-group-${s.key}`}>
                <span className={cn("text-[12px] font-medium text-slate-400 group-hover:text-slate-200 transition-colors", activeIn && "text-slate-200")}>{s.label}</span>
                <ChevronDown className={cn("w-3.5 h-3.5 text-slate-600 transition-transform duration-200", closed && "-rotate-90")} />
              </button>}
              {!closed && <div className={cn("space-y-px", s.label && "ml-2")}>
                {s.items.map((n) => <NavLink key={n.key || n.to} to={n.to} end={n.end} data-testid={`nav-${n.to.replace(/\//g, "-").slice(1)}`} className={({ isActive: a }) => cn("nav-item", a && "active")}><n.icon className="w-4 h-4 opacity-70" /> {n.label}</NavLink>)}
              </div>}
              {closed && activeIn && <div className="ml-2 px-3 py-1 text-[12px] text-white/80 flex items-center gap-2"><span className="signal-dot" />{s.items.find((n) => isItemActive(n, location.pathname))?.label}</div>}
            </div>);
        })}
        {mode.sections.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="apps-empty-state">
            <p className="text-[13px] text-slate-400 leading-relaxed">No apps installed yet.<br />Browse Market to add one.</p>
            <NavLink to="/marketplace" className="inline-block mt-3 text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: "var(--signal)", color: "#fff" }} data-testid="apps-empty-browse">Browse Market</NavLink>
          </div>
        )}
      </nav>
      {mode.key !== "admin" && <RecentsList />}
    </div>);
}

export function BrandLockup() {
  return (
    <div className="px-4 pt-4 pb-3 leading-none" data-testid="brand-lockup">
      <div className="font-heading font-bold text-white text-[12px] tracking-[0.16em] uppercase">Total Operations</div>
      <div className="text-[10px] font-semibold tracking-[0.16em] uppercase mt-1" style={{ color: "var(--signal)" }}>People and systems, moving as one.</div>
    </div>);
}

export function WorkspaceSwitch() {
  const { tenants, activeTenant, gotoTenant, ctx, isPortfolioAdmin } = useAtlas();
  const navigate = useNavigate();
  const own = tenants.filter((t) => !t.support), customers = tenants.filter((t) => t.support);
  const switchTo = async (t) => {
    if (t.support) { try { await api.post("/support/sessions", { tenant_id: t.id, reason: "Switched from workspace selector" }); } catch (e) { return toast.error(apiError(e, "Could not open support session")); } }
    gotoTenant(t.slug || t.id);
  };
  const single = tenants.length <= 1 && !isPortfolioAdmin;
  const Trigger = (
    <button data-testid="tenant-selector-dropdown" disabled={single} className="w-full flex items-center gap-2.5 px-4 py-3 text-left border-y border-white/[0.06] hover:bg-white/[0.03] transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-heading font-semibold text-white truncate leading-tight">{activeTenant?.name}</div>
        {ctx?.support_mode && <div className="text-[11px] truncate mt-0.5 flex items-center gap-1 text-orange-300" data-testid="workspace-subtitle"><LifeBuoy className="w-3 h-3" />Support via Total Operations</div>}
      </div>
      {!single && <ChevronsUpDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
    </button>);
  if (single) return Trigger;
  return (
    <DropdownMenu><DropdownMenuTrigger asChild>{Trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="eyebrow">My workspace</DropdownMenuLabel>
        {own.map((t) => <DropdownMenuItem key={t.id} data-testid={`tenant-option-${t.id}`} onClick={() => switchTo(t)} className="gap-2">{t.id === activeTenant?.id ? <Check className="w-3.5 h-3.5" style={{ color: "var(--signal)" }} /> : <span className="w-3.5" />}<span className="flex-1">{t.name}</span></DropdownMenuItem>)}
        {customers.length > 0 && <><DropdownMenuSeparator /><DropdownMenuLabel className="eyebrow flex items-center gap-1"><LifeBuoy className="w-3 h-3" /> Customer workspaces</DropdownMenuLabel>
          {customers.map((t) => <DropdownMenuItem key={t.id} data-testid={`tenant-option-${t.id}`} onClick={() => switchTo(t)} className="gap-2">{t.id === activeTenant?.id ? <Check className="w-3.5 h-3.5" style={{ color: "var(--signal)" }} /> : <span className="w-3.5" />}<span className="flex-1">{t.name}</span><span className={`chip ${t.active_session ? "border-amber-300 text-amber-700 bg-amber-50" : "border-slate-200 text-slate-500"}`}>{t.active_session ? "session open" : "support"}</span></DropdownMenuItem>)}
          <p className="px-2 py-1.5 text-[10px] text-slate-400">Entering a customer workspace opens an audited support session.</p></>}
        {isPortfolioAdmin && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => navigate("/companies")} className="gap-2" data-testid="companies-overview-link"><LayoutGrid className="w-3.5 h-3.5" /> Companies overview</DropdownMenuItem></>}
      </DropdownMenuContent></DropdownMenu>);
}
